/**
 * Construye el payload de un ensayo de imagen. Cada ensayo usa EXCLUSIVAMENTE la geometría medida
 * al inicio de ese ensayo; si al terminar la geometría es distinta, el ensayo se marca como
 * interrumpido (`viewport_resize`) y nunca se mezclan geometrías.
 */
import type { InterruptionReason, RawGazeSample, TrialCreate, VideoPlaybackMeta } from "@gazelab/shared";
import { sameGeometry, videoTimeAt, type ImageBounds, type PresentedFrame } from "@gazelab/analysis";

/** Registro de reproducción de un ensayo de video (fotogramas presentados + métricas). */
export interface VideoCaptureData {
  frames: ReadonlyArray<PresentedFrame>;
  meta: VideoPlaybackMeta;
}

export interface TrialStart {
  clientTrialId: string;
  imageId: string;
  sequenceIndex: number;
  bounds: ImageBounds;
  /** `performance.now()` del inicio de la captura (cero de `tMs` de las muestras). */
  t0: number;
  startedAtIso: string;
}

export class TrialRecorder {
  constructor(private readonly start: TrialStart) {}

  get info(): TrialStart {
    return this.start;
  }

  /** Fin normal tras la duración configurada. */
  finish(args: {
    samples: RawGazeSample[];
    nowMs: number;
    endBounds: ImageBounds | null;
    visibility: DocumentVisibilityState;
    video?: VideoCaptureData;
  }): TrialCreate {
    const geometryChanged = args.endBounds !== null && !sameGeometry(this.start.bounds, args.endBounds);
    if (geometryChanged) {
      const video = args.video && { ...args.video, meta: { ...args.video.meta, endedNaturally: false } };
      return this.interrupt({ ...args, video, reason: "viewport_resize" });
    }
    return this.payload(args.samples, args.nowMs, args.visibility, "completed", undefined, args.video);
  }

  /** Fin anticipado por una interrupción: nunca se registra como completado. */
  interrupt(args: {
    samples: RawGazeSample[];
    nowMs: number;
    reason: InterruptionReason;
    visibility: DocumentVisibilityState;
    video?: VideoCaptureData;
  }): TrialCreate {
    return this.payload(args.samples, args.nowMs, args.visibility, "interrupted", args.reason, args.video);
  }

  /**
   * Video: cada muestra recibe el tiempo del fotograma visible en `t0 + tMs` (mismo reloj que
   * requestVideoFrameCallback); `null` si aún no se había presentado ninguno.
   */
  static withVideoTime(samples: RawGazeSample[], t0: number, frames: ReadonlyArray<PresentedFrame>): RawGazeSample[] {
    return samples.map((s) => {
      const vt = videoTimeAt(frames, t0 + s.tMs);
      return { ...s, videoTimeMs: vt === null ? null : Math.round(vt * 1000) / 1000 };
    });
  }

  private payload(
    samples: RawGazeSample[],
    nowMs: number,
    visibility: DocumentVisibilityState,
    status: "completed" | "interrupted",
    reason: InterruptionReason | undefined,
    video?: VideoCaptureData,
  ): TrialCreate {
    const durationActualMs = Math.max(0, Math.round(nowMs - this.start.t0));
    return {
      clientTrialId: this.start.clientTrialId,
      imageId: this.start.imageId,
      sequenceIndex: this.start.sequenceIndex,
      startedAt: this.start.startedAtIso,
      durationActualMs,
      status,
      ...(reason ? { interruptionReason: reason } : {}),
      pageVisibilityState: visibility === "hidden" ? "hidden" : "visible",
      bounds: { ...this.start.bounds },
      samples: video ? TrialRecorder.withVideoTime(samples, this.start.t0, video.frames) : samples,
      endedNormally: status === "completed",
      ...(video && { video: video.meta }),
    };
  }
}
